<template>
  <div id="detail-modal" class="uk-modal-container" uk-modal>
    <div class="uk-modal-dialog competency-detail-modal">
      <div class="uk-modal-header">
        <div class="modal-header-content">
          <div class="modal-title-section">
            <i class="uil-file-alt modal-title-icon"></i>
            <h2 class="uk-modal-title"> {{ $t('general.competency_detail') }}</h2>
          </div>
        </div>
      </div>

      <div v-if="competency" class="uk-modal-body">
        <div class="competency-detail-content">
          <!-- User Info Section -->
          <div class="detail-section">
            <div class="section-header">
              <i class="uil-user"></i>
              <h4> {{ $t('general.user_infos') }}</h4>
            </div>
            <div class="section-content">
              <div class="info-item">
                <label> {{ $t('general.name') }}</label>
                <span class="info-value">{{ competency.user_full_name }}</span>
              </div>
              <div class="info-item">
                <label> {{ $t('general.email') }}</label>
                <span class="info-value">{{ competency.user_email }}</span>
              </div>
            </div>
          </div>

          <!-- Competency Info Section -->
          <div class="detail-section">
            <div class="section-header">
              <i class="uil-award"></i>
              <h4> {{ $t('general.competency_information') }}</h4>
            </div>
            <div class="section-content">
              <div class="info-item">
                <label> {{ $t('general.competency_name') }}</label>
                <span class="info-value competency-name">{{ competency.competency_name }}</span>
              </div>
              <div class="info-item">
                <label> {{ $t('general.situation') }}</label>
                <span :class="['status-badge', getStatusClass(competency.status)]">
                  {{ getStatusText(competency.status) }}
                </span>
              </div>


              <div class="info-item full-width">
                <label> {{ $t('general.description') }}: {{ competency.competency_description }}</label>
              </div>
            </div>
          </div>

          <!-- Reviewers Section -->
          <div v-if="competency.reviewers_by && competency.reviewers_by.length > 0" class="detail-section">
            <div class="section-header">
              <i class="uil-users-alt"></i>
              <h4> {{ $t('general.evaluators') }}</h4>
            </div>
            <div class="section-content">

              <div class="info-item">
                <label> {{ $t('general.reviewers_count') }}</label>
                <span class="info-value">{{ competency.reviewers_count }}   </span>
              </div>

              <div class="reviewers-list">
                <div v-for="reviewer in competency.reviewers_by" :key="reviewer.id" class="reviewer-item">
                  <div class="reviewer-content">
                    <div class="reviewer-info">
                      <span class="reviewer-name">{{ reviewer.full_name }}</span>
                      <span class="reviewer-email">{{ reviewer.email }}</span>
                    </div>
                    <div class="reviewer-status">
                      <span :class="['status-badge',  getStatusText(reviewer.status)]">
                        {{ getStatusClassReviewers(reviewer.status) }}
                      </span>
                      <span v-if="reviewer.approved_at" class="reviewer-date">
                        {{ formatDate(reviewer.approved_at) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Assignables Section -->
          <div v-if="competency.competency_assignables && competency.competency_assignables.length > 0"
               class="detail-section">
            <div class="section-header">
              <i class="uil-link"></i>
              <h4> {{ $t('general.competency_assignments') }}</h4>
            </div>
            <div class="section-content">
              <div class="assignables-list">
                <div v-for="assignable in competency.competency_assignables" :key="assignable.id"
                     class="assignable-item">
                  <div class="assignable-content">
                    <span class="assignable-name">{{ assignable.name }}</span>
                    <span class="assignable-type">{{
                        $t(`general.${assignable.assignable_type?.toLowerCase()}`)
                      }}  </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="uk-modal-footer">
        <div class="modal-footer-content">
          <button class="uk-button uk-button-primary modal-close-btn" type="button" @click="closeModal">
            <i class="uil-check"></i>
            {{ $t('general.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";

export default {
  name: "CompetencyDetail",
  props: {
    competency: {
      type: Object,
      default: null
    }
  },
  methods: {
    closeModal() {
      UIkit.modal("#detail-modal").hide();
    },

    getStatusText(status) {
      switch (status) {
        case 'pending':
          return this.$t('general.pending');
        case 'approved':
          return this.$t('general.approved');
        case 'rejected':
          return this.$t('general.rejected');
        default:
          return '--';
      }
    },

    getStatusClass(status) {
      switch (status) {
        case 'pending':
          return 'pending';
        case 'approved':
          return 'approved';
        case 'rejected':
          return 'rejected';
        default:
          return '';
      }
    },
    getStatusClassReviewers(status) {
      switch (status) {
        case 'approved':
          return this.$t('general.approved_by_user');
        case 'rejected':
          return this.$t('general.rejected_by_user');
        default:
          return status;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return moment(dateString).add(3, 'hours').format("DD/MM/YYYY HH:mm")
    }
  }
};
</script>

<style lang="scss" scoped>
// Modal Styles
.competency-detail-modal {
  max-width: 800px !important;
  width: 85%;

  .uk-modal-header {
    background: #f8f9fa;
    color: #2c3e50;
    border-radius: 8px 8px 0 0;
    padding: 16px 20px;
    border-bottom: 1px solid #e8ecf0;

    .modal-header-content {
      display: flex;
      justify-content: center;
      align-items: center;

      .modal-title-section {
        display: flex;
        align-items: center;
        gap: 10px;

        .modal-title-icon {
          font-size: 18px;
          color: #667eea;
        }

        .uk-modal-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
        }
      }
    }
  }

  .uk-modal-body {
    padding: 24px;
    background: #fafbfc;

    .competency-detail-content {
      .detail-section {
        background: white;
        border-radius: 8px;
        margin-bottom: 20px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        border: 1px solid #e8ecf0;
        overflow: hidden;

        &:last-child {
          margin-bottom: 0;
        }

        .section-header {
          background: #f8f9fa;
          padding: 16px 20px;
          border-bottom: 1px solid #e8ecf0;
          display: flex;
          align-items: center;
          gap: 10px;

          i {
            color: #667eea;
            font-size: 16px;
          }

          h4 {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            color: #2c3e50;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }

        .section-content {
          padding: 20px;

          .info-item {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 16px;
            padding-bottom: 12px;
            border-bottom: 1px solid #f1f3f4;

            &:last-child {
              margin-bottom: 0;
              padding-bottom: 0;
              border-bottom: none;
            }

            &.full-width {
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
            }

            label {
              font-size: 13px;
              font-weight: 600;
              color: #6c757d;
              min-width: 140px;
              margin: 0;
            }

            .info-value {
              font-size: 14px;
              color: #2c3e50;
              font-weight: 500;
              text-align: right;
              flex: 1;

              &.competency-name {
                color: #667eea;
                font-weight: 600;
              }

              &.description-text {
                background: #f8f9fa;
                padding: 12px;
                border-radius: 6px;
                border-left: 3px solid #667eea;
                text-align: left;
                line-height: 1.5;
                font-weight: 400;
              }
            }

            .status-badge {
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;

              &.pending {
                background: #fff3cd;
                color: #856404;
                border: 1px solid #ffeaa7;
              }

              &.approved {
                background: #d4edda;
                color: #155724;
                border: 1px solid #c3e6cb;
              }

              &.rejected {
                background: #f8d7da;
                color: #721c24;
                border: 1px solid #f5c6cb;
              }
            }

            .approval-badge {
              padding: 4px 10px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 600;

              &.required {
                background: #d1ecf1;
                color: #0c5460;
                border: 1px solid #bee5eb;
              }

              &.not-required {
                background: #e2e3e5;
                color: #383d41;
                border: 1px solid #d6d8db;
              }
            }
          }

          .assignables-list {
            .assignable-item {
              background: #f8f9fa;
              border: 1px solid #e9ecef;
              border-radius: 6px;
              margin-bottom: 8px;
              transition: all 0.2s ease;

              &:hover {
                background: #e9ecef;
                transform: translateX(2px);
              }

              &:last-child {
                margin-bottom: 0;
              }

              .assignable-content {
                padding: 12px 16px;
                display: flex;
                justify-content: space-between;
                align-items: center;

                .assignable-name {
                  font-weight: 500;
                  color: #2c3e50;
                  font-size: 14px;
                }

                .assignable-type {
                  background: #667eea;
                  color: white;
                  padding: 4px 8px;
                  border-radius: 12px;
                  font-size: 11px;
                  font-weight: 600;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }
              }
            }
          }

          .reviewers-list {
            .reviewer-item {
              background: #f8f9fa;
              border: 1px solid #e9ecef;
              border-radius: 6px;
              margin-bottom: 8px;
              transition: all 0.2s ease;

              &:hover {
                background: #e9ecef;
                transform: translateX(2px);
              }

              &:last-child {
                margin-bottom: 0;
              }

              .reviewer-content {
                padding: 12px 16px;
                display: flex;
                justify-content: space-between;
                align-items: center;

                .reviewer-info {
                  display: flex;
                  flex-direction: column;
                  gap: 2px;

                  .reviewer-name {
                    font-weight: 600;
                    color: #2c3e50;
                    font-size: 14px;
                  }

                  .reviewer-email {
                    font-size: 12px;
                    color: #6c757d;
                    font-weight: 400;
                  }
                }

                .reviewer-status {
                  display: flex;
                  flex-direction: column;
                  align-items: flex-end;
                  gap: 4px;

                  .status-badge {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;

                    &.pending {
                      background: #fff3cd;
                      color: #856404;
                      border: 1px solid #ffeaa7;
                    }

                    &.approved {
                      background: #d4edda;
                      color: #155724;
                      border: 1px solid #c3e6cb;
                    }

                    &.rejected {
                      background: #f8d7da;
                      color: #721c24;
                      border: 1px solid #f5c6cb;
                    }
                  }

                  .reviewer-date {
                    font-size: 11px;
                    color: #6c757d;
                    font-weight: 400;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .uk-modal-footer {
    background: white;
    border-top: 1px solid #e8ecf0;
    padding: 16px 20px;
    border-radius: 0 0 8px 8px;

    .modal-footer-content {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }

    .modal-close-btn {
      background: #667eea;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      font-weight: 600;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;

      &:hover {
        background: #5a6fd8;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
      }

      i {
        font-size: 12px;
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .competency-detail-modal {
    .uk-modal-body {
      padding: 16px;

      .competency-detail-content {
        .detail-section {
          .section-content {
            padding: 16px;

            .info-item {
              flex-direction: column;
              align-items: flex-start;
              gap: 4px;

              label {
                min-width: auto;
              }

              .info-value {
                text-align: left;
              }
            }
          }
        }
      }
    }
  }
}
</style>
